import { useEffect, useRef } from 'react'
import { useMouseEvents } from '~/hooks/useMouseEvents'
import { isInRange } from '~/utils/math'

/**
 * Calls `onClickedOutsideBounds` when a mousedown lands outside the combined
 * bounding box of `element`.
 *
 * `element` and the callback are read through refs and kept out of the effect's
 * deps: callers pass `ref.current` or fresh arrays, so depending on them re-ran
 * the effect every render and — because the callback usually toggles visibility
 * via setState — spun an unbounded render loop after one outside click
 * ("Maximum update depth exceeded").
 */
export const useUserClickedOutsideElementBoundingBox = (
  element: HTMLElement | (HTMLElement | null)[] | null | undefined,
  onClickedOutsideBounds?: () => void,
  shouldIgnore?: (event: MouseEvent) => boolean,
) => {
  const mouseDownEvent = useMouseEvents()

  // Render-phase ref writes are deliberate: no StrictMode/transitions in this
  // app, so no discarded render can strand a stale value here.
  const elementRef = useRef(element)
  elementRef.current = element
  const onClickedOutsideBoundsRef = useRef(onClickedOutsideBounds)
  onClickedOutsideBoundsRef.current = onClickedOutsideBounds
  const shouldIgnoreRef = useRef(shouldIgnore)
  shouldIgnoreRef.current = shouldIgnore

  useEffect(() => {
    const currentElement = elementRef.current
    if (!currentElement) return
    if (!mouseDownEvent) return
    if (shouldIgnoreRef.current?.(mouseDownEvent)) return
    if (!(mouseDownEvent.target instanceof Element)) return
    const isCollection = Array.isArray(currentElement)
    const elements = isCollection ? currentElement : [currentElement]

    // Seed with the identity element for each reducer, not 0 — a 0 seed unions
    // every box with the viewport origin, so clicks in the top-left never read
    // as outside (SlidingListPanel wouldn't dismiss there). An all-null array
    // then leaves an empty box, so isInRange treats every click as outside.
    const extents = {
      left: Infinity,
      right: -Infinity,
      top: Infinity,
      bottom: -Infinity,
    }
    elements.forEach((el) => {
      if (!el) return

      const bounds = el.getBoundingClientRect()
      extents.left = Math.min(extents.left, bounds.left)
      extents.right = Math.max(extents.right, bounds.right)
      extents.top = Math.min(extents.top, bounds.top)
      extents.bottom = Math.max(extents.bottom, bounds.bottom)
    })

    const { clientX, clientY } = mouseDownEvent!

    const insideBounds =
      isInRange(clientX, extents.left, extents.right) &&
      isInRange(clientY, extents.top, extents.bottom)

    if (!insideBounds) {
      onClickedOutsideBoundsRef.current?.()
    }
    // NOTE: element/callback are read through refs on purpose and kept out of
    // the deps — depending on them re-runs this effect every render (see
    // docblock). react-hooks/exhaustive-deps is disabled repo-wide today; when
    // it's re-enabled, suppress it *here* rather than adding them back, which
    // would reintroduce the loop.
  }, [mouseDownEvent])
}
